import { AddButton } from "@/components/ui/AddButton";
import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import type {
  DeclaredSource,
  InventionElementResponse,
} from "@patent-studio/api-types";

const HINT = "mt-1.5 text-[10.5px] leading-relaxed text-muted-foreground";

/** The three answers to "Where is it from?", in the design's words and order. */
const SOURCES: readonly { value: DeclaredSource; label: string }[] = [
  { value: "disclosure", label: "The invention disclosure" },
  { value: "supporting_notes", label: "Supporting notes" },
  { value: "none", label: "Not in any document, I'm adding it" },
];

/** The value the placement control uses for the front of the sequence. */
const AT_THE_START = "";

/** An element's wording, cut to fit one option line. */
function short(text: string): string {
  return text.length > 80
    ? `${text.slice(0, 80).replace(/[ ,;.]+$/, "")}…`
    : text;
}

/** The wording, which is the one thing every element must carry. */
function WordingField({
  fieldRef,
  value,
  onChange,
}: {
  fieldRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <>
      <MicroLabel
        as="label"
        size="sm"
        htmlFor="new-element-text"
        className="block"
      >
        Element text
      </MicroLabel>
      <Textarea
        size="dense"
        id="new-element-text"
        ref={fieldRef}
        rows={2}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        placeholder="e.g. wherein the power cell is rechargeable"
        className="mt-1.5"
      />
    </>
  );
}

/** "Where is it from?": chosen, never defaulted, since it sets the element's tag. */
function SourceField({
  value,
  onChange,
}: {
  value: DeclaredSource | "";
  onChange: (value: DeclaredSource | "") => void;
}) {
  return (
    <>
      <MicroLabel
        as="label"
        size="sm"
        htmlFor="new-element-source"
        className="mt-3 block"
      >
        Where is it from?
      </MicroLabel>
      <Select
        size="dense"
        id="new-element-source"
        value={value}
        onChange={(event) => {
          onChange(event.target.value as DeclaredSource | "");
        }}
        className="mt-1.5"
      >
        <option value="" disabled>
          Choose where it comes from
        </option>
        {SOURCES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
      <p className={HINT}>
        If it isn&rsquo;t in the disclosure or notes it comes in{" "}
        <b className="text-foreground">red</b>. It won&rsquo;t block, it just
        travels with a flag so you&rsquo;re aware.
      </p>
    </>
  );
}

/** "Where does it sit?": the start, or after any element, in sequence order. */
function PlacementField({
  elements,
  after,
  onChange,
}: {
  elements: readonly InventionElementResponse[];
  after: string | null;
  onChange: (after: string | null) => void;
}) {
  return (
    <>
      <MicroLabel
        as="label"
        size="sm"
        htmlFor="new-element-placement"
        className="mt-3 block"
      >
        Where does it sit among the elements?
      </MicroLabel>
      <Select
        size="dense"
        id="new-element-placement"
        value={after ?? AT_THE_START}
        onChange={(event) => {
          onChange(
            event.target.value === AT_THE_START ? null : event.target.value,
          );
        }}
        className="mt-1.5"
      >
        <option value={AT_THE_START}>At the start</option>
        {elements.map((element) => (
          <option key={element.element_id} value={element.element_id}>
            After: &ldquo;{short(element.text)}&rdquo;
          </option>
        ))}
      </Select>
      <p className={HINT}>
        This sets where the element sits in the sequence, and so its number.
        Cards stay grouped by source: grounded elements above, elements needing
        attention below.
      </p>
    </>
  );
}

export interface AddedElement {
  text: string;
  declaredSource: DeclaredSource;
  afterElementId: string | null;
}

/**
 * The control that adds an element, and the form behind it.
 *
 * It asks the three things the design asks: the wording, where it came from,
 * and where it sits in the sequence. The source is chosen, never defaulted,
 * because the tag it sets is the attorney's own declaration.
 */
export function AddElementForm({
  busy,
  elements,
  onAdd,
}: {
  busy: boolean;
  /** The sequence as served, which the placement control offers a place after. */
  elements: readonly InventionElementResponse[];
  /** Resolves when the element is stored. A rejection leaves the form standing. */
  onAdd: (added: AddedElement) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [source, setSource] = useState<DeclaredSource | "">("");
  const [after, setAfter] = useState<string | null>(null);
  const fieldRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) fieldRef.current?.focus();
  }, [open]);

  const last = elements.at(-1)?.element_id ?? null;
  // The chosen element can go while the form is open, on a removal or a
  // refetch; a placement after something that is not there falls back to the
  // end rather than being sent for the server to refuse.
  const placement =
    after !== null && !elements.some((e) => e.element_id === after)
      ? last
      : after;
  useUnsavedDraft(open && text.trim() !== "");

  const close = () => {
    setOpen(false);
    setText("");
    setSource("");
  };

  if (!open) {
    return (
      <AddButton
        emphasis="brand"
        onClick={() => {
          // The end of the sequence, which is where an element goes when the
          // attorney does not say otherwise.
          setAfter(last);
          setOpen(true);
        }}
        className="mt-2"
      >
        Add new element
      </AddButton>
    );
  }

  return (
    <Panel
      as="form"
      className="mt-2"
      onSubmit={(event) => {
        event.preventDefault();
        const wording = text.trim();
        if (wording === "" || source === "") return;
        // Closed only once the element is stored. Closing first would throw away
        // wording the attorney cannot get back: the form is the only copy of it,
        // and a refused write would leave them retyping from memory.
        void onAdd({
          text: wording,
          declaredSource: source,
          afterElementId: placement,
        }).then(close, () => undefined);
      }}
    >
      <WordingField fieldRef={fieldRef} value={text} onChange={setText} />
      <SourceField value={source} onChange={setSource} />
      <PlacementField
        elements={elements}
        after={placement}
        onChange={setAfter}
      />

      <div className="mt-3 flex gap-2">
        <Button
          type="submit"
          variant="secondary"
          size="dense"
          disabled={busy || text.trim() === "" || source === ""}
        >
          Add element
        </Button>
        <Button type="button" variant="secondary" size="dense" onClick={close}>
          Cancel
        </Button>
      </div>
    </Panel>
  );
}
